import { Visitor, User } from '@fund-base/types/auth/auth.types';
import { Plan } from '@fund-base/types/plan/plan.types';
import { VisitorLimitsType } from '@fund-base/types/visitors/visitor-limits.types';

export const DEFAULT_SEARCH_DAILY_LIMIT = 5;
export const DEFAULT_AI_LETTERS_DAILY_LIMIT = 1;
export const DEFAULT_ALLOWED_SEARCH_RESULTS = 5;
export const DEFAULT_TRIAL_EXPIRES_AFTER_DAYS = 7;

const KEY_SEARCHES = 'searches';
const KEY_SEARCHES_LIMIT = 'searches_limit';
const KEY_AI_LETTERS = 'ai_letters';
const KEY_AI_LETTERS_LIMIT = 'ai_letters_limit';
const KEY_USAGE_START_DATE = 'usage_start_date';
const KEY_TRIAL_EXPIRES_AFTER_DAYS = 'trial_expires_after_days';
const KEY_MAX_SEARCH_RESULTS = 'max_search_results';

function formatDate(date: Date): string {
  return date.toISOString().split('T')[0]; // e.g., "2025-04-13"
}

function getNumberOf(key: string): number {
  const today = formatDate(new Date());
  const dateKey = getLastDateKey(key);
  const lastDate = localStorage.getItem(dateKey);

  if (lastDate !== today) {
    localStorage.setItem(key, '0');
    localStorage.setItem(dateKey, today);
    return 0;
  }

  const raw = localStorage.getItem(key);
  const value = parseInt(raw ?? '', 10);
  return isNaN(value) ? 0 : value;
}

function increment(key: string): number {
  const current = getNumberOf(key);
  const updated = current + 1;
  localStorage.setItem(key, updated.toString());
  return updated;
}

export function getNumberOfSearches(): number {
  return getNumberOf(KEY_SEARCHES);
}

export function getNumberOfAILetters(): number {
  return getNumberOf(KEY_AI_LETTERS);
}

export function setVisitorDailySearchLimit(plan?: Plan): number {
  const limit = plan?.allowed_searches_per_day || DEFAULT_SEARCH_DAILY_LIMIT;
  localStorage.setItem(KEY_SEARCHES_LIMIT, limit.toString());
  return limit;
}

export function getVisitorDailySearchLimit(): number {
  const raw = localStorage.getItem(KEY_SEARCHES_LIMIT);
  const value = parseInt(raw ?? '', 10);
  return isNaN(value) ? DEFAULT_SEARCH_DAILY_LIMIT : value;
}

export function setVisitorDailyAILettersLimit(plan?: Plan): number {
  const limit = plan?.allowed_ai_usage_per_day || DEFAULT_AI_LETTERS_DAILY_LIMIT;
  localStorage.setItem(KEY_AI_LETTERS_LIMIT, limit.toString());
  return limit;
}

export function getVisitorDailyAILettersLimit(): number {
  const raw = localStorage.getItem(KEY_AI_LETTERS_LIMIT);
  const value = parseInt(raw ?? '', 10);
  return isNaN(value) ? DEFAULT_AI_LETTERS_DAILY_LIMIT : value;
}

export function setVisitorTrialExpiresAfterDays(plan?: Plan): number {
  const limit = plan?.expires_after_days || DEFAULT_TRIAL_EXPIRES_AFTER_DAYS;
  localStorage.setItem(KEY_TRIAL_EXPIRES_AFTER_DAYS, limit.toString());
  return limit;
}

export function getVisitorTrialExpiresAfterDays(): number {
  const raw = localStorage.getItem(KEY_TRIAL_EXPIRES_AFTER_DAYS);
  const value = parseInt(raw ?? '', 10);
  return isNaN(value) ? DEFAULT_TRIAL_EXPIRES_AFTER_DAYS : value;
}

export function setVisitorMaxSearchResults(plan?: Plan): number {
  const limit = plan?.allowed_search_results || DEFAULT_ALLOWED_SEARCH_RESULTS;
  localStorage.setItem(KEY_MAX_SEARCH_RESULTS, limit.toString());
  return limit;
}

export function getVisitorMaxSearchResults(): number {
  const raw = localStorage.getItem(KEY_MAX_SEARCH_RESULTS);
  const value = parseInt(raw ?? '', 10);
  return isNaN(value) ? DEFAULT_ALLOWED_SEARCH_RESULTS : value;
}

export function getUsageStartDate(): Date | undefined {
  const usageStartDate = localStorage.getItem(KEY_USAGE_START_DATE);
  if (!usageStartDate) return undefined;
  return new Date(usageStartDate);
}

export function setUsageStartDate(date: Date): void {
  localStorage.setItem(KEY_USAGE_START_DATE, date.toISOString());
}

export function incrementSearchCount(): number {
  return increment(KEY_SEARCHES);
}

export function incrementAILettersCount(): number {
  return increment(KEY_AI_LETTERS);
}

function getVisitorSearchLimit(visitorOrUser?: Visitor | User): number {
  if (!visitorOrUser) return DEFAULT_SEARCH_DAILY_LIMIT;

  let plan: Plan | undefined = undefined;
  if ('organization' in visitorOrUser) {
    const user = visitorOrUser as User;
    plan = user?.organization?.membership?.[0]?.plan;
  } else {
    const visitor = visitorOrUser as Visitor;
    plan = visitor?.plan;
  }

  return plan?.allowed_searches_per_day ?? DEFAULT_SEARCH_DAILY_LIMIT;
}

function getVisitorAILetterLimit(visitorOrUser?: Visitor | User): number {
  if (!visitorOrUser) return DEFAULT_AI_LETTERS_DAILY_LIMIT;

  let plan: Plan | undefined = undefined;
  if ('organization' in visitorOrUser) {
    const user = visitorOrUser as User;
    plan = user?.organization?.membership?.[0]?.plan;
  } else {
    const visitor = visitorOrUser as Visitor;
    plan = visitor?.plan;
  }

  return plan?.allowed_ai_usage_per_day ?? DEFAULT_AI_LETTERS_DAILY_LIMIT;
}

export function getVisitorSearchResultLimit(limits?: VisitorLimitsType, visitorOrUser?: Visitor | User): number {
  if (!limits) {
    if (!visitorOrUser) return DEFAULT_ALLOWED_SEARCH_RESULTS;

    let plan: Plan | undefined = undefined;
    if ('organization' in visitorOrUser) {
      const user = visitorOrUser as User;
      plan = user?.organization?.membership?.[0]?.plan;
    } else {
      const visitor = visitorOrUser as Visitor;
      plan = visitor?.plan;
    }

    return plan?.allowed_search_results ?? DEFAULT_ALLOWED_SEARCH_RESULTS;
  }

  return limits.maxSearchResults ?? DEFAULT_ALLOWED_SEARCH_RESULTS;
}

function getVisitorUsageLimit(visitorOrUser?: Visitor | User): number {
  if (!visitorOrUser) return DEFAULT_TRIAL_EXPIRES_AFTER_DAYS;

  let plan: Plan | undefined = undefined;
  if ('organization' in visitorOrUser) {
    const user = visitorOrUser as User;
    plan = user?.organization?.membership?.[0]?.plan;
  } else {
    const visitor = visitorOrUser as Visitor;
    plan = visitor?.plan;
  }

  return plan?.expires_after_days ?? DEFAULT_TRIAL_EXPIRES_AFTER_DAYS;
}

export function visitorHasReachedSearchLimit(limits?: VisitorLimitsType, visitorOrUser?: Visitor | User): boolean {
  if (!limits) {
    const current = getNumberOfSearches();
    const allowed = getVisitorSearchLimit(visitorOrUser);
    return current >= allowed;
  }

  return limits.dailySearches.used >= limits.dailySearches.limit;
}

export function visitorHasReachedAILettersLimit(limits?: VisitorLimitsType, visitorOrUser?: Visitor | User): boolean {
  if (!limits) {
    const current = getNumberOfAILetters();
    const allowed = getVisitorAILetterLimit(visitorOrUser);
    return current >= allowed;
  }

  return limits.dailyAiLetters.used >= limits.dailyAiLetters.limit;
}

export function visitorHasReachedUsageLimit(limits?: VisitorLimitsType, visitorOrUser?: Visitor | User): boolean {
  const usageStartDate = getUsageStartDate();
  if (!usageStartDate) return false;

  const expiresAfterDays = limits ? limits.expiresInDays : getVisitorUsageLimit(visitorOrUser);

  const expiresDate = new Date(usageStartDate);
  expiresDate.setDate(usageStartDate.getDate() + expiresAfterDays);
  return new Date() > expiresDate;
}

export function getVisitorUsageUsed(): number {
  const usageStartDate = getUsageStartDate();
  if (!usageStartDate) return 0;

  const now = new Date();
  const msPerDay = 1000 * 60 * 60 * 24;

  return Math.floor((now.getTime() - usageStartDate.getTime()) / msPerDay);
}

export function updateVisitorLimits(limits: VisitorLimitsType): void {
  localStorage.setItem(KEY_SEARCHES, limits.dailySearches.used.toString());
  localStorage.setItem(KEY_SEARCHES_LIMIT, limits.dailySearches.limit.toString());
  localStorage.setItem(KEY_AI_LETTERS, limits.dailyAiLetters.used.toString());
  localStorage.setItem(KEY_AI_LETTERS_LIMIT, limits.dailyAiLetters.limit.toString());
  localStorage.setItem(KEY_MAX_SEARCH_RESULTS, limits.maxSearchResults.toString());
  localStorage.setItem(KEY_TRIAL_EXPIRES_AFTER_DAYS, limits.expiresInDays.toString());
}

function getLastDateKey(key: string): string {
  return `${key}_date`;
}

export function getVisitorLastSearchDate(): string | null {
  return localStorage.getItem(getLastDateKey(KEY_SEARCHES));
}

export function setVisitorLastSearchDate(date: Date | string): void {
  localStorage.setItem(getLastDateKey(KEY_SEARCHES), date instanceof Date ? formatDate(date) : date);
}

export function getVisitorLastAILetterDate(): string | null {
  return localStorage.getItem(getLastDateKey(KEY_AI_LETTERS));
}

export function setVisitorLastAILetterDate(date: Date | string): void {
  localStorage.setItem(getLastDateKey(KEY_AI_LETTERS), date instanceof Date ? formatDate(date) : date);
}
